<script setup lang="ts">
import { computed, ref } from 'vue';
import { Bar, Line, Pie } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, PointElement, LineElement, ArcElement, ChartType } from 'chart.js';
import { FormDesignItemDto, DisplayType, PropertyDisplayType } from '@/core/store/survey/surveyDesignDtos';
import { SurveyResponseItemDto } from '@/core/store/survey/surveyResponseDtos';
import Survey from '@/core/store/survey/surveyStore';

// Registrar componentes necesarios de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const { isDesign, displayMode } = Survey();

const props = defineProps({
  item: {
    type: Object as () => FormDesignItemDto,
    required: true
  },
  response: {
    type: Object as () => SurveyResponseItemDto,
    default: null,
    required: false
  }
});

// Propiedad computada para obtener los estilos según el modo de visualización
const currentStyles = computed<PropertyDisplayType>(() => {
  switch(displayMode.value) {
    case DisplayType.DISPLAY:
      return props.item.properties?.screen;
    case DisplayType.PDF:
      return props.item.properties?.print;
    case DisplayType.EDIT:
    default:
      return props.item.properties?.input;
  }
});
const width = computed(() => {
  const base = currentStyles.value?.contentStyle?.width || 100;
  return base > 100 ? 100 : base;
});
// Función para formatear números (exactamente igual al original)
const formatNumber = (value: number): string => {
  // Verificar si el número es entero
  if (Number.isInteger(value)) {
    // Formatear con separador de miles con punto
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  } else {
    // Formatear con separador de miles con punto y coma para decimales (2 decimales)
    return value.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    });
  }
};

// Plugin personalizado para las etiquetas de datos
const customDataLabelsPlugin = {
  id: 'customDatalabels',
  afterDatasetsDraw(chart: any) {
    const { ctx, data, chartArea } = chart;
    
    if (!ctx || !data || !data.datasets || !data.datasets[0] || !data.datasets[0].data) return;
    
    // Configuración de fuente usando el tamaño personalizado
    const fontSize = Number(currentStyles.value?.contentStyle?.fontSize) || 12;
    const fontWeight = currentStyles.value?.contentStyle?.bold ? 'bold' : 'normal';
    ctx.font = `${fontWeight} ${fontSize}px Arial`;
    ctx.textAlign = 'center';
    
    // Color de texto del estilo actual
    const textColor = currentStyles.value?.contentStyle?.color || '#000000';
    ctx.fillStyle = textColor;

    const chartType = chart.config.type;
    
    if (chartType === 'pie') {
      // Para gráficos de tarta, dibujamos los valores en el centro de cada segmento
      const dataset = data.datasets[0];
      const meta = chart.getDatasetMeta(0);
      
      for (let i = 0; i < meta.data.length; i++) {
        const arc = meta.data[i];
        const value = dataset.data[i];
        if (value <= 0) continue;
        
        // Calcular posición central del segmento
        const angle = arc.startAngle + (arc.endAngle - arc.startAngle) / 2;
        const radius = arc.outerRadius * 0.6; // Ajustar para que quede dentro del segmento
        
        const x = arc.x + Math.cos(angle) * radius;
        const y = arc.y + Math.sin(angle) * radius;
        
        // Calcular porcentaje
        const total = dataset.data.reduce((acc: number, val: number) => acc + val, 0);
        const percentage = Math.round((value * 100) / total);
        
        // Formatear valor
        const formattedValue = formatNumber(value);
        const valueWithSuffix = props.item.sufix ? `${formattedValue} ${props.item.sufix}` : formattedValue;
        
        // Dibujar texto
        ctx.fillText(valueWithSuffix, x, y - 8);
        ctx.fillText(`(${percentage}%)`, x, y + 8);
      }
    } else if (chartType === 'bar') {
      // Para gráficos de barra, dibujamos los valores sobre cada barra
      const dataset = data.datasets[0];
      const meta = chart.getDatasetMeta(0);
      
      for (let i = 0; i < meta.data.length; i++) {
        const element = meta.data[i];
        const value = dataset.data[i];
        if (value <= 0) continue;
        
        const formattedValue = formatNumber(value);
        const valueWithSuffix = props.item.sufix ? `${formattedValue} ${props.item.sufix}` : formattedValue;
        
        // Dibujar texto sobre la barra
        ctx.fillText(valueWithSuffix, element.x, element.y - 10);
      }
    } else if (chartType === 'line') {
      // Para gráficos de línea, dibujamos los valores sobre cada punto
      const dataset = data.datasets[0];
      const meta = chart.getDatasetMeta(0);
      const dataLength = meta.data.length;
      
      // Texto de valores para cada punto
      for (let i = 0; i < dataLength; i++) {
        const element = meta.data[i];
        const value = dataset.data[i];
        if (value <= 0) continue;
        
        const formattedValue = formatNumber(value);
        const valueWithSuffix = props.item.sufix ? `${formattedValue} ${props.item.sufix}` : formattedValue;
        
        // Por defecto, posicionar encima del punto
        let x = element.x;
        let y = element.y - 15;
        
        if (i === 0) {
          // PRIMER PUNTO: valor a la DERECHA
          x = element.x + 10;
          y = element.y; // A la altura del punto
          ctx.textAlign = 'left';
        } else if (i === dataLength - 1) {
          // ÚLTIMO PUNTO: valor a la IZQUIERDA
          x = element.x - 10;
          y = element.y; // A la altura del punto
          ctx.textAlign = 'right';
        } else {
          // Puntos intermedios: encima del punto
          ctx.textAlign = 'center';
        }
        
        // Dibujar el texto en la posición calculada
        ctx.fillText(valueWithSuffix, x, y);
      }
      
      // Restaurar al valor por defecto
      ctx.textAlign = 'center';
    }
  }
};

// Tipo de gráfico basado en el formato del item
const chartType = computed((): ChartType => {
  switch (props.item.format) {
    case 'barras': return 'bar';
    case 'lineas': return 'line';
    case 'tarta': return 'pie';
    default: return 'bar';
  }
});

// Determinar si la leyenda debe ir a la derecha o abajo
const legendPosition = computed(() => {
  //const width = currentStyles.value?.contentStyle?.width || 0;
  // Para ancho completo (width=0), usar leyenda a la derecha para evitar cortar el gráfico
  return (width.value > 0 && width.value < 90) ? 'bottom' : 'right';
});

// Datos del gráfico con todas las reglas existentes
const chartData = computed(() => {
  const labels = props.item.items?.map(item => item.label) || [];
  
  // Usar exactamente la misma lógica para obtener colores que en el componente actual
  const itemColors = props.item.items?.map(item => 
    item.value ? item.value.trim() : 'rgba(18, 97, 146, 0.7)'
  ) || [];
  
  // Debug de colores

  
  let datasets = [];
  
  if (chartType.value === 'pie') {
    // Para gráficos de tarta - mantener lógica actual
    const data: number[] = [];
    
    // Llenar el array con valores - exactamente como en la versión actual
    for (let i = 0; i < labels.length; i++) {
      if (isDesign.value) {
        const randomValue = (Math.random() * 9000) + 1000;
        data.push(Number(randomValue.toFixed(2)));
      } else {
        const responseData = props.response?.value || [];
        // Asegurar un valor mínimo para evitar problemas de visualización
        data.push(Math.max(responseData[i] || 0, 0.1));
      }
    }

    // Verificar si todos los valores son 0, exactamente como en el componente actual
    const allZero = data.every(value => value === 0);
    if (allZero && data.length > 0) {
      const defaultValue = 100 / labels.length;
      for (let i = 0; i < data.length; i++) {
        data[i] = defaultValue as any; // Usamos type assertion para evitar el error de tipado
      }
    }
    
    // Tooltips personalizados, exactamente como en el componente actual
    const formattedTooltips = data.map((value, index) => {
      const total = data.reduce((acc, val) => acc + val, 0);
      const percentage = Math.round((value * 100) / total);
      const formattedValue = formatNumber(value);
      const valueWithSuffix = props.item.sufix ? `${formattedValue} ${props.item.sufix}` : formattedValue;
      return `${labels[index]}: ${valueWithSuffix} (${percentage}%)`;
    });
    
    // Asegurarnos de que los colores se apliquen correctamente - IMPORTANTE
    const backgroundColors = [...itemColors]; // Crear copia para evitar mutaciones
    const borderColors = [...itemColors]; // Crear copia para evitar mutaciones
    

    datasets = [{
      data,
      backgroundColor: backgroundColors,
      borderColor: borderColors,
      borderWidth: 1,
      hoverOffset: 10,
      weight: 1,
      _tooltipData: formattedTooltips // Mantener tooltip personalizado
    }];
    
    // Agregar logs para depuración
  } else if (chartType.value === 'line') {
    // Para gráficos de línea - mantener lógica actual
    const defaultData = isDesign.value 
      ? labels.map(() => {
          const randomValue = (Math.random() * 9000) + 1000;
          return Number(randomValue.toFixed(2));
        })
      : labels.map((_, index) => (props.response?.value || [])[index] || 0);
    
    datasets = [{
      label: props.item.label || 'Serie',
      data: defaultData,
      backgroundColor: 'transparent',
      borderColor: '#126192', // Usar un solo color para la línea
      borderWidth: 2,
      tension: 0,
      pointRadius: 5,
      pointBackgroundColor: itemColors, // Usar colores personalizados para los puntos
      pointBorderColor: itemColors,    // Usar colores personalizados para los puntos
      pointHoverRadius: 7
    }];
    
    // Log para depuración
  } else if (chartType.value === 'bar') {
    // Para gráficos de barras - mantener lógica actual
    if (isDesign.value) {
      datasets = [{
        label: props.item.label || 'Serie',
        data: labels.map(() => {
          const randomValue = (Math.random() * 9000) + 1000;
          return Number(randomValue.toFixed(2));
        }),
        backgroundColor: itemColors,
        borderColor: itemColors,
        borderWidth: 1
      }];
    } else {
      const responseData = props.response?.value || [];
      datasets = [{
        label: props.item.label || 'Serie',
        data: labels.map((_, index) => responseData[index] || 0),
        backgroundColor: itemColors,
        borderColor: itemColors,
        borderWidth: 1
      }];
    }
    

  }
  return { labels, datasets };
});

// Opciones del gráfico, manteniendo configuraciones existentes
const chartOptions = computed(() => {
  const fontConfig = {
    size: Number(currentStyles.value?.contentStyle?.fontSize) || 12,
    weight: currentStyles.value?.contentStyle?.bold ? 'bold' as const : 'normal' as const
  };

  const textColor = currentStyles.value?.contentStyle?.color || '#000000';
  
  // Base options con toda la configuración actual
  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 300 },
    layout: {
      padding: { 
        top: 40,
        right: 20,
        bottom: 30,
        left: 10
      }
    },
    plugins: {
      legend: {
        display: false,
        position: 'right' as const,
        align: 'start' as const,
        labels: {
          boxWidth: 12,
          padding: 8,
          font: fontConfig,
          color: textColor,
          usePointStyle: true,
          pointStyle: 'circle'
        },
        maxWidth: 120,
        maxHeight: 200,
        rtl: false,
        title: {
          display: false
        }
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: fontConfig,
        bodyFont: fontConfig,
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        cornerRadius: 4,
        callbacks: {
          label: function(context: any) {
            try {
              // Para gráficos de tipo pie, usar tooltips personalizados
              if (chartType.value === 'pie' && context.dataset._tooltipData) {
                return String(context.dataset._tooltipData[context.dataIndex]);
              }
              
              // Para otros tipos de gráficos
              const value = context.raw;
              const formattedValue = formatNumber(value);
              if (props.item.sufix) {
                return String(`${formattedValue} ${props.item.sufix}`);
              }
              return String(formattedValue);
            } catch (e) {
              console.error('Error en tooltip callback:', e);
              return '';
            }
          }
        }
      }
    }
  };
  
  // Obtener los labels y colores para usar en la configuración de opciones
  const chartLabels = chartData.value.labels;
  const chartItemColors = props.item.items?.map(item => 
    item.value ? item.value.trim() : 'rgba(18, 97, 146, 0.7)'
  ) || [];
  
  // Opciones específicas para cada tipo de gráfico
  if (chartType.value === 'pie') {
    //const width = currentStyles.value?.contentStyle?.width || 0;
    
    // Configuración específica para maximizar el uso del espacio horizontal
    const isFullWidth = width.value === 0;
    
    // Calcular espacio para la leyenda basado en el porcentaje de ancho
    const legendSpace = isFullWidth ? 100 : Math.max(30, 100 - width.value); // Mayor ancho, menor espacio para leyenda
    
    // Ajustar la posición de la leyenda basada en el ancho porcentual
    const useLegendOnBottom = width.value > 0 && width.value < 50;
    const legendPos = useLegendOnBottom ? 'bottom' : 'right';
    
    return {
      ...baseOptions,
      responsive: true,
      maintainAspectRatio: false,
      radius: isFullWidth ? '95%' : '90%',
      aspectRatio: isFullWidth ? 1.2 : 1,
      plugins: {
        ...baseOptions.plugins,
        legend: {
          ...baseOptions.plugins.legend,
          display: true,
          position: legendPos as any,
          align: 'center' as any,
          labels: {
            ...baseOptions.plugins.legend.labels,
            font: fontConfig,
            color: textColor,
            padding: 5,
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 8
          },
          maxWidth: isFullWidth ? 140 : 100,
          maxHeight: 200
        }
      },
      layout: {
        padding: {
          top: 5,
          right: legendPos === 'right' ? legendSpace : 5,
          bottom: legendPos === 'bottom' ? 60 : 5,
          left: 5
        }
      },
      cutout: '0%'
    };
  } else if (chartType.value === 'bar') {
    return {
      ...baseOptions,
      scales: {
        y: {
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            color: textColor,
            font: fontConfig,
            callback: function(value: any) {
              // Asegurarnos de devolver un string
              return String(formatNumber(value) + (props.item.sufix ? ` ${props.item.sufix}` : ''));
            }
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: textColor,
            font: fontConfig
          }
        }
      }
    };
  } else if (chartType.value === 'line') {
    return {
      ...baseOptions,
      scales: {
        y: {
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            color: textColor,
            font: fontConfig,
            callback: function(value: any) {
              // Asegurarnos de devolver un string
              return String(formatNumber(value) + (props.item.sufix ? ` ${props.item.sufix}` : ''));
            }
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: textColor,
            font: fontConfig
          }
        }
      }
    };
  }
  
  return baseOptions;
});

// Plugins a utilizar
const plugins = computed(() => {
  return [customDataLabelsPlugin];
});

// Estilo para el contenedor
const chartContainerStyle = computed(() => {
  const align = currentStyles.value?.contentStyle?.align || 'left';
  const isFullWidth = width.value === 0;
  
  const style: { [key: string]: string } = {
    position: 'relative',
    height: chartType.value === 'pie' ? (isFullWidth ? '420px' : '400px') : '350px',
    margin: '1rem 0',
    background: 'white',
    padding: chartType.value === 'pie' ? '0.3rem' : '1rem'
  };
  
  if (width.value > 0) {
    // Ahora width es un porcentaje, no píxeles
    style.width = `${width.value}%`;
    
    // Para gráficos tipo pie, ajustar la altura basándose en el ancho porcentual
    if (chartType.value === 'pie') {
      // Altura entre 300px y 600px según el porcentaje (para mantener proporción)
      const heightValue = 300 + (width.value * 3); // 100% = 600px, 50% = 450px, etc.
      style.height = `${Math.min(600, heightValue)}px`;
    }
    
    switch (align) {
      case 'center':
        style.marginLeft = 'auto';
        style.marginRight = 'auto';
        break;
      case 'right':
        style.marginLeft = 'auto';
        style.marginRight = '0';
        break;
      default:
        style.marginLeft = '0';
        style.marginRight = 'auto';
        break;
    }
  } else {
    style.width = '100%';
  }
  
  return style;
});

// Añadimos una clase CSS específica para gráficos tipo pie
const chartClass = computed(() => {
  return chartType.value === 'pie' ? 'pie-chart-wrapper' : '';
});
</script>

<template>
  <div class="chart-container" :style="chartContainerStyle">
    <!-- Renderizar el tipo correcto de gráfico según chartType -->
    <Bar v-if="chartType === 'bar'" 
      :key="'bar-' + Date.now()"
      :data="chartData" 
      :options="chartOptions as any"
      :plugins="plugins" 
    />
    <Line v-else-if="chartType === 'line'" 
      :key="'line-' + Date.now()"
      :data="chartData" 
      :options="chartOptions as any"
      :plugins="plugins" 
    />
    <!-- Para gráficos tipo pie, usamos un contenedor especial sin transform que corta el gráfico -->
    <div v-else-if="chartType === 'pie'" class="pie-chart-container">
      <Pie
        :key="'pie-' + Date.now()"
        :data="chartData" 
        :options="chartOptions as any"
        :plugins="plugins"
        :class="chartClass"
      />
    </div>
  </div>
</template>

<style scoped>
.chart-container {
  position: relative;
  overflow: hidden;
}

.pie-chart-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pie-chart-wrapper {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  overflow: visible;
}

.pie-chart-wrapper canvas {
  max-width: 100% !important;
  max-height: 100% !important;
}
</style>

<script lang="ts">
export default {
  name: 'SurveyItemChart'
}
</script>